import { z } from "zod";

// Consent audit trail (LEGAL-03). Mirror of the Prisma ConsentPurpose enum. Kept
// here (not imported from @chotca/db) so the schema package stays free of the DB
// client and can run in the browser.
export const CONSENT_PURPOSES = [
  "TERMS_ACCEPT",
  "PRIVACY_ACCEPT",
  "EMPLOYER_AGREEMENT",
  "APPLY_SHARE_CONTACT",
  "INVITE_REVEAL_CONTACT",
  "OPEN_TO_WORK",
  "MARKETING_EMAIL",
  "ANALYTICS",
  "KYC_DOCUMENT",
  "ORDER_CONFIRM",
  "GUARDIAN_CONSENT",
] as const;

export const consentPurposeSchema = z.enum(CONSENT_PURPOSES);
export type ConsentPurpose = z.infer<typeof consentPurposeSchema>;

// POST /me/consents — grant or withdraw a purpose. The server fills docVersion,
// ipAddress, userAgent and source; those are NEVER accepted from the client (a
// spoofed docVersion would void the audit trail). Only docSlug/refId hints come
// from the body, and even docSlug is validated against the legal doc registry.
export const consentSourceSchema = z.enum(["mobile", "employer", "web"]);
export type ConsentSource = z.infer<typeof consentSourceSchema>;

export const recordConsentRequestSchema = z.object({
  purpose: consentPurposeSchema,
  granted: z.boolean(),
  docSlug: z.string().min(1).max(64).optional(),
  refId: z.string().min(1).max(128).optional(),
  source: consentSourceSchema.optional(),
});
export type RecordConsentRequest = z.infer<typeof recordConsentRequestSchema>;

// A single stored consent row as returned to the client.
export const consentRecordSchema = z.object({
  id: z.string(),
  purpose: consentPurposeSchema,
  granted: z.boolean(),
  docSlug: z.string().nullable(),
  docVersion: z.string().nullable(),
  refId: z.string().nullable(),
  source: z.string(),
  createdAt: z.string(),
});
export type ConsentRecordDto = z.infer<typeof consentRecordSchema>;

// GET /me/consents — current state per purpose (newest row wins) plus full
// history. Powers the privacy screen (LEGAL-12).
export const consentStatusSchema = z.object({
  purpose: consentPurposeSchema,
  granted: z.boolean(), // current effective state (false if never granted)
  updatedAt: z.string().nullable(), // createdAt of the newest row, null if none
});
export type ConsentStatus = z.infer<typeof consentStatusSchema>;

export const consentsResponseSchema = z.object({
  statuses: z.array(consentStatusSchema),
  history: z.array(consentRecordSchema),
});
export type ConsentsResponse = z.infer<typeof consentsResponseSchema>;
